import { useState } from 'react'
import { Button } from '../../../shared/ui'
import { deleteProgram, saveProgram } from '../../../lib/outreach/api'
import { DAY_KEYS } from '../../../lib/outreach/types'
import type { Program, ProgramKind, Sequence, SiteRef } from '../../../lib/outreach/types'
import { toast } from '../../../lib/toast'
import { ConfirmButton, ErrorLine, Field, reportError } from './shared'
import { draftFromProgram, validateDraft } from './programDraft'
import type { ProgramDraft } from './programDraft'
import { RampEditor } from './RampEditor'

const KINDS: ReadonlyArray<{ value: ProgramKind; label: string }> = [
  { value: 'editorial', label: 'Editorial' },
  { value: 'sales', label: 'Sales' },
  { value: 'other', label: 'Other' },
]

const DAY_LABELS: Record<(typeof DAY_KEYS)[number], string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
}

interface Props {
  program: Program | null
  sites: SiteRef[]
  sequences: Sequence[]
  timezone: string
  onSaved: (p: Program) => void
  onDeleted: (id: string) => void
  onCancel: () => void
}

export function ProgramForm({
  program,
  sites,
  sequences,
  timezone,
  onSaved,
  onDeleted,
  onCancel,
}: Props) {
  const [draft, setDraft] = useState<ProgramDraft>(() => draftFromProgram(program))
  const [submitted, setSubmitted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [apiError, setApiError] = useState('')

  const patch = (p: Partial<ProgramDraft>) => setDraft((d) => ({ ...d, ...p }))
  const result = validateDraft(draft)
  const errors = submitted ? result.errors : {}

  const save = async () => {
    setSubmitted(true)
    setApiError('')
    if (!result.input) return
    setBusy(true)
    try {
      const saved = await saveProgram(program?.id ?? null, result.input)
      toast('Program saved', 'success')
      onSaved(saved)
    } catch (e) {
      setApiError(reportError(e))
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!program) return
    setApiError('')
    setBusy(true)
    try {
      await deleteProgram(program.id)
      toast('Program deleted', 'success')
      onDeleted(program.id)
    } catch (e) {
      setApiError(reportError(e))
      setBusy(false)
    }
  }

  return (
    <form
      className="os-root"
      aria-label={program ? 'Edit program' : 'New program'}
      onSubmit={(e) => {
        e.preventDefault()
        void save()
      }}
    >
      <h3 className="os-name">{program ? 'Edit program' : 'New program'}</h3>

      <div className="os-grid">
        <Field id="pf-name" label="Name" error={errors.name}>
          <input
            id="pf-name"
            className="settings-input"
            value={draft.name}
            onChange={(e) => patch({ name: e.target.value })}
          />
        </Field>
        <Field id="pf-kind" label="Kind">
          <select
            id="pf-kind"
            className="form-select"
            value={draft.kind}
            onChange={(e) => patch({ kind: e.target.value as ProgramKind })}
          >
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </Field>
        <Field id="pf-site" label="Site">
          <select
            id="pf-site"
            className="form-select"
            value={draft.siteId}
            onChange={(e) => patch({ siteId: e.target.value })}
          >
            <option value="">No site</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id="pf-from"
          label="From label"
          hint="Shown to the sending routine as the sender label."
        >
          <input
            id="pf-from"
            className="settings-input"
            value={draft.fromLabel}
            onChange={(e) => patch({ fromLabel: e.target.value })}
          />
        </Field>
        <Field id="pf-seq" label="Sequence">
          <select
            id="pf-seq"
            className="form-select"
            value={draft.sequenceId}
            onChange={(e) => patch({ sequenceId: e.target.value })}
          >
            <option value="">No sequence</option>
            {sequences.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field id="pf-cap" label="Daily cap" error={errors.dailyCap}>
          <input
            id="pf-cap"
            className="settings-input"
            type="number"
            min={0}
            value={draft.dailyCap}
            onChange={(e) => patch({ dailyCap: e.target.value })}
          />
        </Field>
      </div>

      <RampEditor
        rows={draft.ramp}
        onChange={(ramp) => patch({ ramp })}
        dailyCap={draft.dailyCap}
        timezone={timezone}
        error={errors.ramp}
      />

      <div className="os-grid">
        <Field
          id="pf-cool"
          label="Cooldown days"
          hint="No email to the same company within this many days of the last one."
          error={errors.cooldown}
        >
          <input
            id="pf-cool"
            className="settings-input"
            type="number"
            min={0}
            value={draft.cooldown}
            onChange={(e) => patch({ cooldown: e.target.value })}
          />
        </Field>
        <Field
          id="pf-bounce"
          label="Bounce stop (%)"
          hint="Pause when the 7 day bounce rate is above this."
          error={errors.bounce}
        >
          <input
            id="pf-bounce"
            className="settings-input"
            type="number"
            min={0}
            max={100}
            step="any"
            value={draft.bouncePct}
            onChange={(e) => patch({ bouncePct: e.target.value })}
          />
        </Field>
        <Field
          id="pf-bounce-min"
          label="Bounce stop minimum sends"
          hint="Ignore the bounce rate until the last 7 days hold at least this many sends."
          error={errors.bounceMin}
        >
          <input
            id="pf-bounce-min"
            className="settings-input"
            type="number"
            min={0}
            step={1}
            value={draft.bounceMin}
            onChange={(e) => patch({ bounceMin: e.target.value })}
          />
        </Field>
      </div>

      <div className="settings-row">
        <label className="os-check" htmlFor="pf-never">
          <input
            id="pf-never"
            type="checkbox"
            checked={draft.neverSame}
            onChange={(e) => patch({ neverSame: e.target.checked })}
          />
          Never send to the same address twice
        </label>
      </div>

      <fieldset className="settings-row os-fieldset">
        <legend className="settings-label">Send days (in the business timezone: {timezone})</legend>
        <div className="os-days">
          {DAY_KEYS.map((k) => (
            <label className="os-check" key={k} htmlFor={`pf-day-${k}`}>
              <input
                id={`pf-day-${k}`}
                type="checkbox"
                checked={draft.days[k]}
                onChange={(e) => patch({ days: { ...draft.days, [k]: e.target.checked } })}
              />
              {DAY_LABELS[k]}
            </label>
          ))}
        </div>
        {result.warnings.map((w) => (
          <div className="os-warn" key={w}>
            {w}
          </div>
        ))}
      </fieldset>

      <Field
        id="pf-phrases"
        label="Blocked phrases"
        hint="One per line. Added to the business list."
      >
        <textarea
          id="pf-phrases"
          className="settings-input"
          rows={4}
          value={draft.phrases}
          onChange={(e) => patch({ phrases: e.target.value })}
        />
      </Field>

      <div className="settings-row">
        <label className="os-check" htmlFor="pf-active">
          <input
            id="pf-active"
            type="checkbox"
            checked={draft.active}
            onChange={(e) => patch({ active: e.target.checked })}
          />
          Active (the program sends mail when this is on)
        </label>
      </div>

      <ErrorLine>{apiError}</ErrorLine>
      <div className="os-bar">
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving...' : 'Save program'}
        </Button>
        <Button variant="secondary" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        {program && (
          <span className="os-bar-end">
            <ConfirmButton
              label="Delete program"
              confirmLabel="Yes, delete"
              note="This removes the program and its enrolments."
              disabled={busy}
              onConfirm={() => void remove()}
            />
          </span>
        )}
      </div>
    </form>
  )
}
